import {
  AutoClosingDropdown,
  FormSection,
  PrimaryButton,
  SecondaryPageTitle,
  TextInput,
  useAlert,
} from "@wit/mpesa-ui-components";
import { DropdownType } from "@wit/mpesa-ui-components/lib/components/dropdown/dropdown.component";
import { FormSectionRow } from "@wit/mpesa-ui-components/lib/components/form-section/form-section.component";
import { AlertTypeEnum } from "@wit/mpesa-ui-components/lib/context/alert/alert.context";
import { Formik, FormikHelpers, FormikErrors } from "formik";
import { TFunction } from "i18next";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { array, object, string } from "yup";
import { RoutesEnum } from "../../../../routes/routes.constants";
import { isSFCMarket, unicodeChars } from "../../../../shared/shared.utils";
import * as Yup from "yup";
import IdentityApi from "../identity.api";
import {
  ICreateIdentityRequest,
  ISingleIdentityRequest,
  StatusEnums,
  TypeEnums,
  CaptureBackSideEnums,
} from "../identity.interfaces";
import { useHistory } from "react-router-dom";
import { ConfigContext } from "../../../../app.component";
import { THEMES } from "../../../../shared/renderer.utils";
import { useUsersValidationRules } from "../../../admin/users/hooks/use-users-validation-rules.hook";
import { IFullNameValidation } from "../../../../shared/shared.interfaces";
import { IdentityActions } from "../identity.store";
import { normalizeErrorMessage } from "../../../../shared/utils/errorMessageHelper.utils";
import { cleanBackendMessage } from "../../../../shared/utils/cleanBackendErrorMessage.utils";

/**
 * method to validate user form
 */

/**
 * AddUserPage component
 */
const AddIdentityPage = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const [showAlert, hideAlert, setAlertProps] = useAlert();
  const history = useHistory();
  const { config } = useContext(ConfigContext);

  const identityValidationSchema = Yup.object().shape({
    identity: Yup.array().of(
      Yup.object().shape({
        documentId: Yup.string()
          .required(t("pages.addIdentity.errors.requiredField"))
          .matches(/^\d+$/, t("pages.addIdentity.errors.requiredNumbers"))
          .max(3, t("pages.addIdentity.errors.requiredMinNumberLength")),
        en: Yup.string()
          .required(t("pages.addIdentity.errors.requiredField"))
          .min(3, t("pages.addIdentity.errors.requiredMinLength"))
          .max(70, t("pages.addIdentity.errors.requiredMaxLength"))
          .test("only-letters", t("pages.addIdentity.errors.requiredAlphabets"), value =>
            value ? unicodeChars(value) : true,
          ),
        fr: Yup.string()
          .required(t("pages.addIdentity.errors.requiredField"))
          .min(3, t("pages.addIdentity.errors.requiredMinLength"))
          .max(70, t("pages.addIdentity.errors.requiredMaxLength")),
      }),
    ),
  });
  const { fullName: FULL_NAME_VALIDATION_RULES } = useUsersValidationRules();

  function getRows(
    identity: ISingleIdentityRequest,
    errors: FormikErrors<ICreateIdentityRequest>,
    setFieldValue: any,
    idx: number,
  ): FormSectionRow[] {
    const rows = [
      {
        label: t("pages.addIdentity.formSection.documentIdLabel"),
        displayComponent: null,
        editingComponent: (
          <TextInput
            id={"documentId"}
            maxLength={50}
            onChange={e => setFieldValue(`identity[${idx}].documentId`, e.target.value)}
            error={
              errors.identity && errors.identity[idx]
                ? (errors.identity[idx] as ISingleIdentityRequest).documentId
                : undefined
            }
            placeholder={t("pages.addIdentity.formSection.documentIdPlaceholder")}
            value={identity.documentId}
            required={true}
          />
        ),
      },
      {
        label: t("pages.addIdentity.formSection.englishNameLabel"),
        displayComponent: null,
        editingComponent: (
          <TextInput
            id={"en"}
            onChange={e => setFieldValue(`identity[${idx}].en`, e.target.value)}
            placeholder={t("pages.addIdentity.formSection.englishPlaceholder")}
            error={
              errors.identity && errors.identity[idx] ? (errors.identity[idx] as ISingleIdentityRequest).en : undefined
            }
            value={identity.en}
            required={true}
          />
        ),
      },
      {
        label: t("pages.addIdentity.formSection.frenchNameLabel"),
        displayComponent: null,
        editingComponent: (
          <TextInput
            id={"fr"}
            onChange={e => setFieldValue(`identity[${idx}].fr`, e.target.value)}
            placeholder={t("pages.addIdentity.formSection.frenchPlaceholder")}
            error={
              errors.identity && errors.identity[idx] ? (errors.identity[idx] as ISingleIdentityRequest).fr : undefined
            }
            value={identity.fr}
            required={true}
          />
        ),
      },
      {
        label: t("pages.addIdentity.formSection.captureBackLabel"),
        displayComponent: null,
        editingComponent: (
          <AutoClosingDropdown
            options={[
              { key: "TRUE", label: t("commons.captureStatusType.TRUE") },
              { key: "FALSE", label: t("commons.captureStatusType.FALSE") },
            ]}
            dropdownType={DropdownType.RECTANGULAR_NORMAL}
            selectOption={opt => setFieldValue(`identity[${idx}].captureBackSide`, opt.key === "TRUE")}
            label={
              identity.captureBackSide !== undefined
                ? t(`commons.captureStatusType.${identity.captureBackSide ? "TRUE" : "FALSE"}`)
                : t("pages.captureStatusType.formSection.captureBackPlaceholder")
            }
            hasValue={!!identity.captureBackSide}
          />
        ),
      },
      {
        label: t("pages.addIdentity.formSection.docStandardLabel"),
        displayComponent: null,
        editingComponent: (
          <AutoClosingDropdown
            options={[
              { key: "STANDARD_DOC", label: t("commons.identityType.STANDARD_DOC") },
              { key: "NON_STANDARD_DOC", label: t("commons.identityType.NON_STANDARD_DOC") },
            ]}
            dropdownType={DropdownType.RECTANGULAR_NORMAL}
            selectOption={opt => setFieldValue(`identity[${idx}].documentStandard`, opt.key)}
            label={
              identity.documentStandard
                ? t(`commons.identityType.${identity.documentStandard}`)
                : t("pages.addIdentity.formSection.docStandardPlaceholder")
            }
            hasValue={!!identity.documentStandard}
          />
        ),
      },
    ];
    return rows;
  }

  const toTitleCase = (str: string) => str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());

  /**
   * Add users request
   */
  const addIdentity = (values: ICreateIdentityRequest, actions: FormikHelpers<ICreateIdentityRequest>) => {
    actions.setSubmitting(true);

    // Map over each identity and add documentType
    const payload: ISingleIdentityRequest[] = values.identity.map(identityItem => {
      const enValue = identityItem.en?.trim();
      const documentType = enValue
        ? enValue
            .split(/\s+/)
            .join("_")
            .toUpperCase()
        : "UNKNOWN_DOCUMENT_TYPE";

      return {
        ...identityItem,
        documentType,
      };
    });

    IdentityApi.methods
      .createIdentity(payload)
      .finally(() => actions.setSubmitting(false))
      .then(res => {
        dispatch(IdentityActions.creators.addIdentityAction(res.data));
        setAlertProps({
          type: AlertTypeEnum.SUCCESS,
          title: t("pages.addIdentity.alerts.addIdentitySuccess"),
        });
        showAlert();
        history.push(RoutesEnum.MANAGE_IDENTITY_DOCS);
      })
      .catch((error: any) => {
        let backendMessage =
          error?.data?.status?.httpCode === 404 && error?.data?.status?.businessCode === 4046
            ? t("pages.addIdentity.formSection.alerts.addLdapUserError")
            : error?.data?.status?.message || t("pages.addIdentity.formSection.alerts.addIdentityError");
        backendMessage = cleanBackendMessage(backendMessage);
        const httpCode = error?.data?.status?.httpCode;
        backendMessage = normalizeErrorMessage(backendMessage, httpCode);
        setAlertProps({
          title: backendMessage,
          type: AlertTypeEnum.ERROR,
        });
        showAlert();
      });
  };

  return (
    <>
      <SecondaryPageTitle
        displayInitialsCircle={false}
        title={t("pages.addIdentity.title")}
        paddingLeft={200}
        goBackFn={() => window.history.back()}
      />
      <Formik
        onSubmit={addIdentity}
        initialValues={
          {
            identity: [
              {
                documentId: "",
                en: "",
                fr: "",
                status: undefined,
                documentStandard: TypeEnums.NON_STANDARD_DOC,
                captureBackSide: false,
              },
            ],
          } as ICreateIdentityRequest
        }
        validateOnBlur={true}
        validateOnChange={true}
        validationSchema={identityValidationSchema}
        render={({ handleSubmit, isSubmitting, values, setFieldValue, errors, setValues, isValid }) => (
          <>
            <FormContainer onSubmit={handleSubmit}>
              {values.identity.map((identity, idx) => (
                <FormSectionCard>
                  <FormSection
                    title={
                      <FormSectionCardTitle>
                        <div>{t("pages.addIdentity.formSection.title")}</div>
                      </FormSectionCardTitle>
                    }
                    rows={getRows(identity, errors, setFieldValue, idx)}
                    isEditing={true}
                  />
                </FormSectionCard>
              ))}
              <ButtonRow>
                <ButtonContainer>
                  <PrimaryButton
                    redTheme={true}
                    type="submit"
                    disabled={!isValid || isSubmitting}
                    titleLabel={t("pages.addIdentity.formSection.buttonLabel")}
                    loading={isSubmitting}
                    safaricom={config?.theme === THEMES.SAFARICOM}
                  />
                </ButtonContainer>
              </ButtonRow>
            </FormContainer>
          </>
        )}
      />
    </>
  );
};

export default AddIdentityPage;

const FormTitle = styled("div")`
  font-family: Vodafone Rg;
  font-size: 16px;
  color: ${props => props.theme.palette.midGrey};
  margin-bottom: 32px;
  width: 50%;
  @media (max-width: 768px) {
    width: 75%;
  }
  @media (max-width: 600px) {
    width: 100%;
  }
`;

const IconContainer = styled("div")<{ color?: string; size?: string }>`
  width: ${props => (props.size ? props.size : "24px")};
  height: ${props => (props.size ? props.size : "24px")};
  stroke: ${props => (props.color ? props.color : props.theme.palette.vodafoneRed)};
  svg {
    width: ${props => (props.size ? props.size : "24px")};
    height: ${props => (props.size ? props.size : "24px")};
  }
`;

const FormContainer = styled("form")`
  margin-top: 156px;
  margin-left: 245px;
  margin-right: 288px;
  margin-bottom: 156px;
  @media (max-width: 1024px) {
    margin-left: 137px;
    margin-right: 24px;
  }
  @media (max-width: 768px) {
    margin-left: 94px;
    margin-right: 24px;
  }
`;

const FormSectionCardTitle = styled("div")`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  > span {
    font-family: Vodafone Rg;
    font-size: 22px;
    color: ${props => props.theme.palette.darkGrey};
  }
`;

const FormSectionCard = styled("div")`
  border-radius: 8px;
  box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.1), 0 3px 6px 0 rgba(142, 142, 142, 0.23);
  background-color: ${props => props.theme.palette.white};
  padding: 18px 24px;

  > div > div > span > div > div > button {
    display: none;
  }

  :hover {
    box-shadow: 0 6px 20px 0 rgba(0, 0, 0, 0.15), 0 4px 6px 0 rgba(0, 0, 0, 0.2);

    > div > div > span > div > div > button {
      display: flex;
    }
  }

  :not(:last-child) {
    margin-bottom: 24px;
  }

  div {
    border-bottom: none !important;
  }

  > div:first-child > div:first-child > span:first-child {
    width: 100%;
  }
`;
const ButtonRow = styled("div")`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;

  svg {
    stroke: ${props => props.theme.palette.vodafoneRed};
  }
`;

const ButtonContainer = styled("div")`
  width: fit-content;
  margin-left: auto;
`;
